import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Chip, IconButton, Alert, CircularProgress, Tooltip, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  BugReport,
  Warning,
  Error,
  Info,
  Refresh,
  Download,
  FilterList,
  Clear
} from '@mui/icons-material';
import { api } from '@services/authService';
import { formatForDisplay } from '@utils/dateFormatter';

interface SystemError {
  id: string;
  type: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
  clinic?: string;
  userId?: string;
  stack?: string;
}

const AdminErrors: React.FC = () => {
  const [errors, setErrors] = useState<SystemError[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    severity: 'all',
    type: 'all',
    clinic: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchErrors();
  }, []);

  const fetchErrors = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/errors');
      
      if (response.data.success) {
        setErrors(response.data.errors || []);
      } else {
        setError(response.data.error || 'Eroare la incarcarea logurilor');
      }
    } catch (err: any) {
      console.error('❌ Eroare la incarcarea logurilor:', err);
      // Folosim date mock pentru demonstratie
      const mockErrors: SystemError[] = [
        {
          id: '1',
          type: 'Database Connection',
          message: 'Conexiunea la baza de date a esuat',
          severity: 'high',
          timestamp: '2025-08-01T10:30:00Z',
          clinic: 'Clinica Veterinara Happy Pets',
          userId: 'user-123'
        },
        {
          id: '2',
          type: 'Email Service',
          message: 'Serviciul de email nu raspunde',
          severity: 'medium',
          timestamp: '2025-08-01T09:15:00Z',
          clinic: 'VetCare Plus'
        },
        {
          id: '3',
          type: 'Payment Gateway',
          message: 'Timeout la procesarea platii',
          severity: 'low',
          timestamp: '2025-08-01T08:45:00Z'
        },
        {
          id: '4',
          type: 'API Rate Limit',
          message: 'Prea multe cereri API',
          severity: 'critical',
          timestamp: '2025-08-01T07:30:00Z',
          userId: 'user-456'
        }
      ];
      setErrors(mockErrors);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return <Error />;
      case 'medium':
        return <Warning />;
      case 'low':
        return <Info />;
      default:
        return <BugReport />;
    }
  };

  const filteredErrors = errors.filter(error => {
    const matchesSeverity = filters.severity === 'all' || error.severity === filters.severity;
    const matchesType = filters.type === 'all' || error.type === filters.type;
    const matchesClinic = filters.clinic === 'all' || error.clinic === filters.clinic;
    const matchesSearch = searchTerm === '' || 
      error.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      error.type.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSeverity && matchesType && matchesClinic && matchesSearch;
  });

  const handleExportErrors = () => {
    const csvContent = [
      ['ID', 'Type', 'Message', 'Severity', 'Timestamp', 'Clinic', 'User ID'],
      ...filteredErrors.map(error => [
        error.id,
        error.type,
        error.message,
        error.severity,
        error.timestamp,
        error.clinic || '',
        error.userId || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `system-errors-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            🐛 Monitorizare Erori Sistem
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestionare si monitorizare erori din sistem
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchErrors}
            disabled={loading}
          >
            Reimprospateaza
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExportErrors}
            disabled={filteredErrors.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
            Filtre
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Cauta"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Severitate</InputLabel>
                <Select
                  value={filters.severity}
                  onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
                  label="Severitate"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="critical">Critic</MenuItem>
                  <MenuItem value="high">Inalt</MenuItem>
                  <MenuItem value="medium">Mediu</MenuItem>
                  <MenuItem value="low">Scazut</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tip</InputLabel>
                <Select
                  value={filters.type}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  label="Tip"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="Database Connection">Conexiune DB</MenuItem>
                  <MenuItem value="Email Service">Serviciu Email</MenuItem>
                  <MenuItem value="Payment Gateway">Gateway Plati</MenuItem>
                  <MenuItem value="API Rate Limit">Limit API</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={() => {
                  setFilters({ severity: 'all', type: 'all', clinic: 'all' });
                  setSearchTerm('');
                }}
                fullWidth
              >
                Reseteaza
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="error.main">
                {errors.filter(e => e.severity === 'critical' || e.severity === 'high').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Erori Critice/Inalte
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                {errors.filter(e => e.severity === 'medium').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Erori Medii
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {errors.filter(e => e.severity === 'low').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Erori Scazute
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary.main">
                {errors.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Erori
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Errors Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Severitate</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>Mesaj</TableCell>
                    <TableCell>Clinica</TableCell>
                    <TableCell>Utilizator</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Actiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredErrors.map((error) => (
                    <TableRow key={error.id}>
                      <TableCell>
                        <Chip
                          icon={getSeverityIcon(error.severity)}
                          label={error.severity.toUpperCase()}
                          color={getSeverityColor(error.severity) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {error.type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {error.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {error.clinic || '-'}
                      </TableCell>
                      <TableCell>
                        {error.userId || '-'}
                      </TableCell>
                      <TableCell>
                        {formatForDisplay(error.timestamp)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Vezi detalii">
                          <IconButton size="small">
                            <Info />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {!loading && filteredErrors.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                Nu s-au gasit erori care sa corespunda filtrelor.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default AdminErrors; 
